import {
  useCallback,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import { useUserPreferences } from "src/hooks/userPreferences/hooks/useUserPreferences";
import {
  enqueueWorkspaceUserWrite,
  getWorkspaceUserWriteQueueVersion,
  readWorkspaceUserPreferencesOverlay,
  subscribeWorkspaceUserWriteQueue,
} from "src/hooks/userPreferences/workspaceUserWriteQueue";

import {
  EMPTY_HEADER_SEARCH,
  clearRecentSearches,
  listRecentlyViewed,
  pushRecentSearch,
  pushViewedItem,
  pushViewedPerson,
  withHeaderSearchLists,
} from "./headerSearchRecentEntries";
import {
  acquireHeaderSearchRecorder,
  releaseHeaderSearchRecorder,
  setHeaderSearchRecorder,
  updateHeaderSearchRecorder,
} from "./headerSearchRecorder";

/**
 * Header search recents for this user in this workspace.
 * Changes only headerSearch on the WORKSPACE_USER snapshot at send time.
 * enableHomeV2 off: empty lists and no preference write.
 */
export const useHeaderSearchRecents = () => {
  const { enableHomeV2 } = useWorkspaceFeatureFlags();
  const enabled = enableHomeV2 === true;
  const {
    loading,
    workspaceId,
    userId,
    mergedPreferences,
    workspaceUserPreferences,
    sendWorkspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const loadingRef = useRef(loading);
  const prefsRef = useRef(workspaceUserPreferences);
  const scopeRef = useRef({ workspaceId, userId });
  loadingRef.current = loading;
  prefsRef.current = workspaceUserPreferences;
  scopeRef.current = { workspaceId, userId };

  const enqueue = useCallback(
    (
      mutate: (
        current: user_prefs.HeaderSearchPreference | null | undefined
      ) => user_prefs.HeaderSearchPreference
    ) => {
      if (!enabled) {
        return Promise.resolve(false);
      }
      return enqueueWorkspaceUserWrite({
        scope: scopeRef.current,
        readScope: () => scopeRef.current,
        field: "headerSearch",
        readProps: () => prefsRef.current,
        apply: (latest) => ({
          ...(latest ?? {}),
          headerSearch: mutate(latest?.headerSearch),
        }),
        send: sendWorkspaceUserPreferences ?? updatePreferencesForWorkspaceUser,
        isLoading: () => loadingRef.current,
      });
    },
    [enabled, sendWorkspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  const rememberSearch = useCallback(
    (item: user_prefs.RecentSearch) =>
      enqueue((current) => pushRecentSearch(current, item)),
    [enqueue]
  );

  const rememberPerson = useCallback(
    (person: user_prefs.RecentlyViewedPerson) =>
      enqueue((current) => pushViewedPerson(current, person)),
    [enqueue]
  );

  const rememberOpenedPerson = useCallback(
    (
      person: user_prefs.RecentlyViewedPerson,
      search: user_prefs.RecentSearch
    ) =>
      enqueue((current) =>
        pushRecentSearch(pushViewedPerson(current, person), search)
      ),
    [enqueue]
  );

  const rememberViewed = useCallback(
    (item: user_prefs.RecentlyViewedItem) =>
      enqueue((current) => pushViewedItem(current, item)),
    [enqueue]
  );

  const rememberOpenedView = useCallback(
    (item: user_prefs.RecentlyViewedItem, search: user_prefs.RecentSearch) =>
      enqueue((current) =>
        pushRecentSearch(pushViewedItem(current, item), search)
      ),
    [enqueue]
  );

  const clearSearches = useCallback(
    () => enqueue((current) => clearRecentSearches(current)),
    [enqueue]
  );

  const registrationId = useRef<number | null>(null);
  const recorderRef = useRef({
    rememberSearch,
    rememberPerson,
    rememberOpenedPerson,
    rememberOpenedView,
    rememberViewed,
  });

  useLayoutEffect(() => {
    recorderRef.current = {
      rememberSearch,
      rememberPerson,
      rememberOpenedPerson,
      rememberOpenedView,
      rememberViewed,
    };
    const id = registrationId.current;
    if (id == null) {
      return;
    }
    updateHeaderSearchRecorder(id, recorderRef.current);
  }, [
    rememberOpenedPerson,
    rememberOpenedView,
    rememberPerson,
    rememberSearch,
    rememberViewed,
  ]);

  useLayoutEffect(() => {
    if (!enabled) {
      setHeaderSearchRecorder(null);
      return undefined;
    }
    const id = acquireHeaderSearchRecorder(recorderRef.current);
    registrationId.current = id;
    updateHeaderSearchRecorder(id, recorderRef.current);
    return () => {
      releaseHeaderSearchRecorder(id);
      if (registrationId.current === id) {
        registrationId.current = null;
      }
    };
  }, [enabled]);

  const writeQueueVersion = useSyncExternalStore(
    subscribeWorkspaceUserWriteQueue,
    getWorkspaceUserWriteQueueVersion
  );
  const visiblePreferences = loading
    ? undefined
    : readWorkspaceUserPreferencesOverlay(
        { workspaceId, userId },
        mergedPreferences
      );
  void writeQueueVersion;

  const storedHeaderSearch =
    !enabled || loading
      ? EMPTY_HEADER_SEARCH
      : visiblePreferences?.headerSearch;
  const headerSearch = withHeaderSearchLists(storedHeaderSearch);

  return {
    loading,
    recentSearches: headerSearch.recentSearches,
    recentlyViewedPeople: headerSearch.recentlyViewedPeople,
    recentlyViewed: listRecentlyViewed(storedHeaderSearch),
    rememberSearch,
    rememberPerson,
    rememberOpenedPerson,
    rememberViewed,
    rememberOpenedView,
    clearSearches,
  };
};
